<template>
    <div class="chat-window-manager">
        <GridChatWindowManager
            ref="gridWindow"
            v-if="viewMode === VIEW_MODE_TYPES.GRID"
            :contactList="contactList"
            :toggleConversation="toggle"
            :isLive="isLive"
            :contact="cloneActiveContact"
        />

        <ContactSwitchScreen
            v-if="showContactSwitch"
            @close="() => (showContactSwitch = false)"
            :toggleConversation="toggle"
        />
        <MultiChatViewer
            v-if="multiChatViewerMode.enabled"
            :agent-id="multiChatViewerMode.value"
            @close="() => (multiChatViewerMode.enabled = false)"
            @change-agent-group="changeAgentGroup"
        />
    </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";
import * as _ from "lodash";
import { fakeConversation } from "../../../utils/utils";
import GridChatWindowManager from "./GridChatWindowManager.vue";
import ContactSwitchScreen from "./ContactSwitchScreen.vue";
import SystemPubSub from "../../../utils/systemPubSub";
import MultiChatViewer from "./multi-chat-viewer/MultiChatViewer.vue";
import StringUtils from "../../../utils/StringUtils";
import log2 from "../../../utils/log2";
import { UtilFunctions } from "../../../utils/UtilFunctions";

const log = log2("ChatWindowManager");
const VIEW_MODE_TYPES = {
    GRID: "grid",
    TABLE: "table",
};

export default {
    name: "ChatWindowManager",
    components: { MultiChatViewer, ContactSwitchScreen, GridChatWindowManager },
    props: {
        isLive: {
            type: Boolean,
            default: false,
        },
        viewMode: {
            type: String,
            default: VIEW_MODE_TYPES.GRID,
        },
        contact: {
            type: Object,
            default: () => ({}),
        },
    },
    data: () => ({
        VIEW_MODE_TYPES,
        beep: Audio,
        loading: false,
        lastDataFetchTS: Date.now(),
        cloneActiveContact: null,
        fetchedIntervalId: null,
        currentRequestId: null,
        showContactSwitch: false,
    }),
    mounted() {
        this.initConversation();
        // press tab to switch between contacts
        const keydownHandler = (e) => {
            const editor = _.get(
                this.$refs.gridWindow,
                "$refs.chatWindow.$refs.chat.$refs.messageArea.$refs.messageArea.$refs.editor"
            );
            const isTabEnabled = editor?.isTabEnabled();
            const isInput = e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA";
            if (e.key === "Tab" && !isTabEnabled && !isInput) {
                if (!this.showContactSwitch) {
                    e.preventDefault();
                    this.showContactSwitch = !this.showContactSwitch;
                }
            }
            if (e.key === "Escape") {
                if (this.multiChatViewerMode.enabled) return;
                if (this.showContactSwitch) this.showContactSwitch = false;
            }
        };
        window.addEventListener("keydown", keydownHandler);
        const sub = SystemPubSub.stream.subscribe((message) => {
            if (message.type === SystemPubSub.MESSAGE_TYPES.OPEN_CONTACT_SWITCH_SCREEN) {
                this.showContactSwitch = true;
            }
        });
        this.$once("hook:beforeDestroy", () => {
            window.removeEventListener("keydown", keydownHandler);
            sub.unsubscribe();
        });
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            selectedSubject: (state) => state.subject.selectedSubject,
            site: (state) => state.site.site,
            isRoot: (state) => state.auth.user.role.isRoot,
            fakeMode: (state) => state.app.fakeMode,
            socketConnected: (state) => state.socketStatus.socket_connected,
            rows: (state) => state.chat.latestRows,
            contactLists: (state) => state.chat.contactLists,
            watchingContactIds: (state) => state.chat.watchingContactIds,
            activeContact: (state) => state.chat.activeContact,
            multiChatViewerMode: (state) => state.chat.multiChatViewerMode,
        }),
        ...mapGetters({
            findConversationByVisitorId: "chat/findConversationByVisitorId",
            isDarkMode: "user/isDarkMode",
            findConversation: "chat/findConversation",
        }),
        contactList() {
            if (this.fakeMode) return [fakeConversation()];
            else return this.contactLists;
        },
    },
    methods: {
        ...mapActions({
            endConversation: "chat/endConversation",
            setLatestRows: "chat/setLatestRows",
            joinContactConversation: "chat/joinContactConversation",
            watchContactConversation: "chat/watchContactConversation",
            leaveContactConversation: "chat/leaveContactConversation",
            setActiveContact: "chat/setActiveContact",
            multiBeepConversation: "chat/multiBeepConversation",
            getConversationList: "chat/getConversationList",
            setContactPaginationOffset: "chat/setContactPaginationOffset",
            setMultiChatViewerMode: "chat/setMultiChatViewerMode",
        }),
        initConversation() {
            log.debug("initConversation", {
                activeContact: this.activeContact,
                contact: this.contact,
                isLive: this.isLive,
            });
            this.cloneActiveContact = this.isLive ? this.activeContact : this.contact;
        },
        toggle(contact) {
            const activeContact = this.cloneActiveContact;
            if (!activeContact || activeContact._id !== contact._id) {
                this.multiBeepConversation([contact._id]);
                this.joinContactConversation(contact);
            } else {
                this.setActiveContact(null);
            }
        },
        changeAgentGroup(id) {
            this.setMultiChatViewerMode({ enabled: true, value: id, type: "agent" });
        },
    },

    watch: {
        activeContact: {
            deep: true,
            immediate: true,
            handler(newVal, oldVal) {
                const isEq = StringUtils.hashCodeObj(newVal || {}) === StringUtils.hashCodeObj(oldVal || {});
                const diff = UtilFunctions.difference(newVal || {}, oldVal || {});
                log("activeContact model changed", { newVal, oldVal, isEq, diff });
                log("activeContact model changed 2", {
                    oldUsers: oldVal?.lastConversation?.users,
                    newUsers: newVal?.lastConversation?.users,
                });

                this.initConversation();
            },
        },
    },
};
</script>
<style>
.chat-window-manager {
    width: 100%;
    height: 100%;
}
</style>
